import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import {
  LOCAL_CONTEXT_KEYS,
  isSignInSurface,
  purgeSupabaseAuthStorage,
  resolveSignInRouteForPath,
  shouldPropagateSignOut,
} from "@/lib/authSessionLifecycle";
import { logPortalAuthDiagnostic } from "@/lib/portalAuthDiagnostics";
import { clearActiveLoginSession, clearAllPortalPinChallenges } from "@/lib/activeLoginSession";


interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isRecoverySession: boolean;
  clearRecoverySession: () => void;
  signOut: (details?: { reason?: string; source?: string }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({ 
  user: null, 
  session: null, 
  loading: true,
  isRecoverySession: false,
  clearRecoverySession: () => {},
  signOut: async () => {}
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<number>(0);
  const [isRecoverySession, setIsRecoverySession] = useState(() => {
    // Check localStorage on init for cross-tab persistence
    return localStorage.getItem('recovery_session_active') === 'true';
  });
  const queryClient = useQueryClient();

  // Helper to check if session was created via password recovery by decoding JWT AMR
  const isRecoverySessionFromToken = (session: Session | null): boolean => {
    if (!session?.access_token) return false;
    
    try {
      const payload = JSON.parse(atob(session.access_token.split('.')[1]));
      const amr = payload.amr as Array<{ method: string }> | undefined;
      return amr?.some(entry => entry.method === 'recovery') ?? false;
    } catch {
      return false;
    }
  };

  // Cross-tab listeners: recovery flag changes AND sign-out propagation.
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'recovery_session_active') {
        const isRecovery = e.newValue === 'true';
        console.log('🔄 Storage event: recovery_session_active changed to', isRecovery);
        setIsRecoverySession(isRecovery);
        return;
      }

      // Another tab signed out — the Supabase auth token was removed.
      if (shouldPropagateSignOut(e)) {
        void logPortalAuthDiagnostic('cross_tab_signout_detected', { reason: 'auth_storage_cleared', source: 'storage_event' });
        console.log('🔄 Sign-out detected in another tab — clearing this tab');
        setUser(null);
        setSession(null);
        setIsRecoverySession(false);
        queryClient.clear();
        if (typeof window !== 'undefined' && !isSignInSurface(window.location.pathname)) {
          window.location.replace(resolveSignInRouteForPath(window.location.pathname));
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [queryClient]);


  const clearRecoverySession = () => {
    localStorage.removeItem('recovery_session_active');
    setIsRecoverySession(false);
  };

  const signOut = async (details: { reason?: string; source?: string } = {}) => {
    const pathname = typeof window !== 'undefined' ? window.location.pathname : 'unknown';
    const reason = details.reason ?? 'user_requested';
    const source = details.source ?? pathname;
    void logPortalAuthDiagnostic('sign_out_requested', { reason, source, scope: 'global' });
    // Clear all recovery/session flags and stale device-context blobs so a
    // different user logging in does not inherit a factory/outlet selection.
    for (const key of LOCAL_CONTEXT_KEYS) {
      try { localStorage.removeItem(key); } catch { /* storage unavailable */ }
    }
    clearAllPortalPinChallenges();
    try { sessionStorage.removeItem('just_changed_password'); } catch { /* ignore */ }
    setIsRecoverySession(false);

    try {
      await clearActiveLoginSession();
    } catch (e) {
      console.warn('Active login session cleanup failed:', e);
    }

    const target = resolveSignInRouteForPath(
      typeof window !== 'undefined' ? window.location.pathname : '/'
    );

    try {
      // Best-effort cleanup; never allowed to block the sign-out.
      await supabase.rpc('cleanup_expired_outlet_sessions');
    } catch (e) {
      console.warn('cleanup_expired_outlet_sessions failed during sign-out:', e);
    }

    // Sign out globally - invalidates all sessions across devices/tabs.
    // Capture (never throw on) the result: a network-level failure leaves the
    // SDK's stored session in place, so we always follow up locally.
    let globalSignOutOutcome = 'failed';
    try {
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) console.warn('Global sign-out returned an error:', error.message);
      else globalSignOutOutcome = 'succeeded';
    } catch (e) {
      console.warn('Global sign-out threw:', e);
    }

    let localSignOutOutcome = 'failed';
    try {
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (!error) localSignOutOutcome = 'succeeded';
    } catch (e) {
      console.warn('Local sign-out threw:', e);
    }

    void logPortalAuthDiagnostic('auth_signout_completed', {
      reason, source, scope: 'global_and_local',
      status: `global:${globalSignOutOutcome};local:${localSignOutOutcome}`,
    });

    // Belt and braces: purge any residual Supabase auth token material so a
    // revoked session can never be re-hydrated on the next page load.
    purgeSupabaseAuthStorage();

    // Clear all cached data
    queryClient.clear();

    setUser(null);
    setSession(null);

    // Hard navigation: discards every in-memory cache and half-mounted tree.
    if (typeof window !== 'undefined') {
      window.location.replace(target);
    }

    // Note: Device binding persists - device stays bound to outlet
  };

  useEffect(() => {
    // Safety timeout: force loading to false after 3 seconds
    const loadingTimeout = setTimeout(() => {
      console.log('⚠️ Auth loading timeout reached, forcing loading: false');
      setLoading(false);
    }, 3000);

    // Listen for auth state changes FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (event !== 'INITIAL_SESSION' || newSession) {
        void logPortalAuthDiagnostic(`supabase_auth_${event.toLowerCase()}`, {
          reason: event === 'SIGNED_OUT' ? 'supabase_emitted_signed_out' : 'auth_state_changed',
          source: 'supabase_auth_state_change',
          auth_event: event,
        });
      }
      console.log('🔐 Auth state change:', event, 'User:', newSession?.user?.email);
      
      // CRITICAL: Detect password recovery sessions - these are restricted
      if (event === 'PASSWORD_RECOVERY') {
        console.log('🔒 Password recovery session detected - marking as restricted');
        localStorage.setItem('recovery_session_active', 'true');
        setIsRecoverySession(true);
      }
      
      // Also check JWT AMR for recovery sessions (handles cross-tab SIGNED_IN events)
      const tokenIsRecovery = isRecoverySessionFromToken(newSession);
      const storageIsRecovery = localStorage.getItem('recovery_session_active') === 'true';
      
      if (tokenIsRecovery || storageIsRecovery) {
        console.log('🔒 Recovery session detected from', tokenIsRecovery ? 'JWT AMR' : 'localStorage');
        localStorage.setItem('recovery_session_active', 'true');
        setIsRecoverySession(true);
      }
      
      // Debounce token refresh to prevent excessive refresh attempts
      if (event === 'TOKEN_REFRESHED') {
        const now = Date.now();
        if (now - lastRefresh < 300000) { // 5 minutes
          console.log('⏭️ Skipping duplicate token refresh (within 5 min window)');
          return;
        }
        setLastRefresh(now);
        console.log('✅ Token refreshed');
      }
      
      // Only propagate to React state when identity actually changes.
      // Supabase v2 re-emits SIGNED_IN and TOKEN_REFRESHED on tab focus with
      // a brand-new session object; propagating those would remount the app
      // (ProtectedRoute spinner flash). The SDK still updates its internal
      // session store, so supabase.auth.getSession() / edge calls stay fresh.
      const nextUserId = newSession?.user?.id ?? null;
      const alwaysPropagate =
        event === 'SIGNED_OUT' ||
        event === 'USER_UPDATED' ||
        event === 'PASSWORD_RECOVERY' ||
        event === 'INITIAL_SESSION';

      // Use functional updates so we compare against the LATEST state
      // (the effect closure captures the initial user, which is stale).
      setUser((prevUser) => {
        const prevId = prevUser?.id ?? null;
        if (alwaysPropagate || prevId !== nextUserId) {
          return newSession?.user ?? null;
        }
        return prevUser;
      });
      setSession((prevSession) => {
        const prevId = prevSession?.user?.id ?? null;
        if (alwaysPropagate || prevId !== nextUserId) {
          return newSession;
        }
        return prevSession;
      });
      setLoading(false); // Always clear loading gate on any auth event
      clearTimeout(loadingTimeout);
    });

    // Then get the current session with timeout
    const sessionPromise = supabase.auth.getSession().then((result) => {
      const reason = result.error
        ? 'session_read_failed'
        : result.data.session
          ? 'session_found'
          : 'no_session_found';
      const errorCode = typeof result.error?.code === 'string' && /^[a-z0-9_-]{1,80}$/i.test(result.error.code)
        ? result.error.code
        : null;
      void logPortalAuthDiagnostic('supabase_session_hydration', {
        reason,
        source: 'auth_context_initial_hydration',
        error_code: errorCode,
      });
      return result;
    }).catch((error: unknown) => {
      const code = error && typeof error === 'object' && 'code' in error &&
        typeof (error as { code?: unknown }).code === 'string' &&
        /^[a-z0-9_-]{1,80}$/i.test((error as { code: string }).code)
        ? (error as { code: string }).code
        : null;
      void logPortalAuthDiagnostic('supabase_session_hydration', {
        reason: 'session_read_failed',
        source: 'auth_context_initial_hydration',
        error_code: code,
      });
      throw error;
    });
    const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve(null), 2000));

    Promise.race([sessionPromise, timeoutPromise])
      .then((result: any) => {
        const session = result?.data?.session || null;
        console.log('📋 Initial session check:', session?.user?.email || 'No session');
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        if (session) clearTimeout(loadingTimeout);
      })
      .catch((err) => {
        console.error('❌ Session check failed:', err);
        setLoading(false);
        clearTimeout(loadingTimeout);
      });

    return () => {
      subscription.unsubscribe();
      clearTimeout(loadingTimeout);
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, loading, isRecoverySession, clearRecoverySession, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
